/**
 * cotizaciones.ts
 * Compras (REQUI) module — Cotizaciones section: RFQ rounds, the comparative
 * matrix, per-supplier column metadata, quotation attachments, and the
 * consolidation-round (cotización consolidada por rubro) flow.
 *
 * refactor/compras-by-section — pure move out of index.ts (no shape/behavior change).
 */
import { z } from 'zod';

import { CaseItemResponseSchema } from './requisiciones.js';

/** GET /procurement/cases/:id/matrix?roundId= — "cuadro comparativo" (SDD §6). */
export const MATRIX_CELL_STATUSES = [
  'cotizada',
  'desestimada',
  'sustituto_propuesto',
  'vencida',
  'parcial',
  'adjudicada',
] as const;

export const MatrixCellSchema = z.object({
  quoteLineId: z.string().uuid(),
  supplierId: z.string().uuid(),
  status: z.enum(MATRIX_CELL_STATUSES),
  price: z.number().nullable(),
  currency: z.string().nullable(),
  suggested: z.boolean(),
  declineReason: z.string().nullable(),
  /** Only meaningful when status === 'parcial' — the qty this quote actually
   * offers, so the UI can adjudicar_cantidad_parcial (T10) with the right
   * qtyAwarded instead of silently full-awarding via adjudicar_item (T9). */
  qtyOffered: z.number().nullable(),
  /** Plazo de entrega en días (quotation.delivery_days, vía quote_lines.quotation_id) — informativo, repetido por celda; ver también columnMeta abajo (por proveedor, no por celda). */
  deliveryDays: z.number().nullable(),
  /** ISO datetime (quotation.validity_date) — para el listado de "cotizaciones cargadas" del paso ②. */
  validUntil: z.string().nullable(),
  /**
   * Section 7 (repo audit) — post-award read-back, only meaningful once
   * status === 'adjudicada'. `deviationReason` is the code persisted onto
   * the winning quote_line at award time (quote_lines.deviation_reason);
   * `deviationFlags` is the item's own non-blocking marks
   * (case_items.deviation_flags — section 1's "controles blandos"), copied
   * onto every cell of that item so the celda already-adjudicada can show it
   * without a second lookup. Both null when there's nothing to show.
   */
  deviationReason: z.string().nullable(),
  deviationFlags: z.array(z.string()).nullable(),
});
export type MatrixCell = z.infer<typeof MatrixCellSchema>;

export const MatrixRowSchema = z.object({
  itemId: z.string().uuid(),
  cells: z.array(MatrixCellSchema),
  desierto: z.boolean(),
  /**
   * N° de ítem — el backend ordena `rows` desierto-primero (SDD §6, "desiertos
   * al final"), orden que le sirve a ComparativeMatrix.vue pero NO a
   * CargaPreciosGrid.vue (muestra todo junto, sin separar desiertos): sin este
   * campo, esa grilla no tiene de dónde sacar un orden propio estable, y una
   * fila "salta" de posición apenas deja de ser desierta (bug real,
   * verificado 2026-08-19 — "se mueve de lugar la celda y la columna" al Tab).
   */
  lineNo: z.number(),
  /**
   * Rediseño de cotización — proveedores invitados a cotizar este ítem que
   * todavía no tienen `cell` (sin quote_line real). La grilla ②.2/③ los
   * dibuja como celda vacía y editable; ver build-comparative-matrix.ts.
   */
  invitedSupplierIds: z.array(z.string().uuid()),
});
export type MatrixRow = z.infer<typeof MatrixRowSchema>;

export const SupplierFooterSchema = z.object({
  supplierId: z.string().uuid(),
  total: z.number(),
  coversAllItems: z.boolean(),
});
export type SupplierFooter = z.infer<typeof SupplierFooterSchema>;

/**
 * Un adjunto (PDF/imagen) resuelto de compras.quotation_attachment, vía
 * identity.file — openspec/requi/adjuntos-y-auditoria.md §3, alcance mínimo.
 * Mismo shape que MilestoneAttachmentResponseSchema (servicios): solo
 * fileId + metadata resuelta en lectura, nunca duplicada en la tabla propia.
 */
export const QuotationAttachmentResponseSchema = z.object({
  id: z.string().uuid(),
  fileId: z.string().uuid(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  createdAt: z.string().datetime().or(z.date()),
});
export type QuotationAttachmentResponse = z.infer<typeof QuotationAttachmentResponseSchema>;

/** Request body de `POST /compras/quotations/:id/attachments` — fileIds ya subidos vía POST /files (kernel). */
export const AttachQuotationFilesSchema = z
  .object({
    fileIds: z.array(z.string().uuid()).min(1, 'At least one fileId is required'),
  })
  .strict();
export type AttachQuotationFilesInput = z.infer<typeof AttachQuotationFilesSchema>;

/**
 * Column-level metadata (SDD §5/§6) — plazo de entrega, condición de pago y
 * adjuntos son atributos de la `quotation` cabecera, compartidos por TODA la
 * columna de un proveedor en una ronda, no por celda (a diferencia de
 * MatrixCell.deliveryDays, que sigue existiendo por compatibilidad con el
 * indicador por celda). Si el proveedor tiene más de una quotation en la
 * misma ronda (recotización), se usa la más reciente con status='received'.
 * `attachments` vacío = "sin respaldo documental" (§3, controles blandos) —
 * el frontend deriva la marca de `attachments.length === 0`, no hay un
 * campo booleano separado que pueda desincronizarse de la lista real.
 */
export const SupplierColumnMetaSchema = z.object({
  supplierId: z.string().uuid(),
  deliveryDays: z.number().nullable(),
  paymentTerms: z.string().nullable(),
  attachments: z.array(QuotationAttachmentResponseSchema),
  /** null si el proveedor está invitado pero todavía no tiene quotation (sin cotizar aún). */
  quotationId: z.string().uuid().nullable(),
  /**
   * Bug real, prod (2026-08-29): "en ningún lugar puedo editar validez y la
   * fecha" — currency/validUntil de la cabecera, para que CargaPreciosGrid.vue
   * tenga una fuente ÚNICA y persistida (no derivada de la primera celda) al
   * mostrar/editar estos campos incluso después de la primera cotización.
   */
  currency: z.string().nullable(),
  validUntil: z.string().datetime().nullable(),
});
export type SupplierColumnMeta = z.infer<typeof SupplierColumnMetaSchema>;

export const BestCombinationLineSchema = z.object({
  itemId: z.string().uuid(),
  supplierId: z.string().uuid(),
  quoteLineId: z.string().uuid(),
  price: z.number(),
});
export type BestCombinationLine = z.infer<typeof BestCombinationLineSchema>;

export const BestCombinationSchema = z.object({
  total: z.number(),
  perItem: z.array(BestCombinationLineSchema),
  bestSingleSupplierTotal: z.number().nullable(),
  savingsVsBestSingleSupplier: z.number().nullable(),
});
export type BestCombination = z.infer<typeof BestCombinationSchema>;

export const ComparativeMatrixResponseSchema = z.object({
  rows: z.array(MatrixRowSchema),
  supplierIds: z.array(z.string().uuid()),
  footerBySupplier: z.array(SupplierFooterSchema),
  bestCombination: BestCombinationSchema,
  columnMeta: z.array(SupplierColumnMetaSchema),
});
export type ComparativeMatrixResponse = z.infer<typeof ComparativeMatrixResponseSchema>;

/** GET /procurement/cases/:id/rounds (Sesión 4) — every rfq_round ever opened for the case. */
export const CaseRoundResponseSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid().nullable(),
  categoryId: z.string().uuid().nullable(),
  roundNo: z.number(),
  status: z.enum(['abierta', 'cerrada']),
  isConsolidation: z.boolean(),
  deadline: z.string(),
  closedAt: z.string().nullable(),
  itemCount: z.number(),
  itemIds: z.array(z.string().uuid()),
  quotationCount: z.number(),
  lineCount: z.number(),
  /**
   * requi-cotizacion-consolidada-por-rubro (Slice C, task 3.14, design.md D5)
   * — set exactly when `isConsolidation` is true. `itemIds`/`itemCount` above
   * are already restricted to THIS SA's own items of the round; `caseCount`
   * is the round's total distinct SA count.
   */
  consolidation: z
    // requi-consolidacion-multirubro (2026-09-25): categoryId nullable — null
    // when the consolidation round's items span more than one rubro.
    .object({ consolidationNo: z.number(), categoryId: z.string().uuid().nullable(), caseCount: z.number() })
    .nullable(),
});
export type CaseRoundResponse = z.infer<typeof CaseRoundResponseSchema>;

/**
 * requi-cotizacion-consolidada-por-rubro (Slice C, task 3.16, design.md D8)
 * — one row per `(itemMasterId, unit)` group in the consolidated RFQ view.
 * `qty` is a decimal string (same convention as every other qty field in
 * this file); `itemIds` are the underlying items summed into this row.
 */
export const SummedLineResponseSchema = z.object({
  key: z.string(),
  itemMasterId: z.string().uuid().nullable(),
  itemCode: z.string().nullable(),
  unit: z.string(),
  description: z.string(),
  qty: z.string(),
  itemIds: z.array(z.string().uuid()),
});
export type SummedLineResponse = z.infer<typeof SummedLineResponseSchema>;

/**
 * requi-cotizacion-consolidada-por-rubro Slice A (design.md task 1.5) — the
 * "Consolidar compras" tray (`GET /procurement/demand-by-rubro`). Extends
 * `CaseItemResponseSchema` with the case-level facts the tray needs so every
 * row is traceable back to its SA without a second round-trip: `caseCode`/
 * `caseTitle` (the SA), `requesterId`/`requesterName`, `costCenterId`/
 * `costCenterName`, `itemCode` (item_master's code — null this slice, no
 * task wires that join yet), `neededBy` (the case's own delivery deadline).
 *
 * `requesterId` is REQUIRED (never nullable/optional, unlike the trio on
 * `CaseResponseSchema`) — every `purchase_cases` row always has one.
 * `requesterName`/`costCenterName` are `.nullable()` (a lookup miss resolves
 * to `null`, never omitted) — same degrade-gracefully posture as
 * `PendingPurchaseOrderRow.supplierName`'s raw-id fallback, but here the
 * consumer needs to distinguish "no cost center on this SA" from "lookup
 * failed", so `null` beats a fabricated fallback string.
 */
export const DemandItemSchema = CaseItemResponseSchema.extend({
  caseCode: z.string(),
  caseTitle: z.string(),
  requesterId: z.string().uuid(),
  requesterName: z.string().nullable(),
  costCenterId: z.string().uuid().nullable(),
  costCenterName: z.string().nullable(),
  itemCode: z.string().nullable(),
  neededBy: z.string().nullable(),
});
export type DemandItem = z.infer<typeof DemandItemSchema>;

/**
 * One rubro's worth of tray demand — `suggestedSupplierIds` sourced from
 * sumi's `supplier_category` (design.md D14), consumed by
 * `AsignarProveedoresTable`'s v-model pre-fill starting Slice C.
 */
export const DemandByRubroResponseSchema = z.object({
  groups: z.array(
    z.object({
      categoryId: z.string().uuid(),
      categoryName: z.string(),
      itemCount: z.number(),
      caseCount: z.number(),
      suggestedSupplierIds: z.array(z.string().uuid()),
      items: z.array(DemandItemSchema),
    }),
  ),
});
export type DemandByRubroResponse = z.infer<typeof DemandByRubroResponseSchema>;

/**
 * requi-cotizacion-consolidada-por-rubro (Slice C, task 3.9, design.md
 * Interfaces/Contracts) — `POST /procurement/consolidation-rounds` request
 * body. `.strict()` rejects an unexpected field outright, same posture as
 * every other write-request schema in this file.
 *
 * requi-consolidacion-multirubro (2026-09-25): dropped `categoryId` — the
 * buyer selects items, which may span different rubros (SDD invariant 15 is
 * now "Consolidación: mismo tenant."); the round's own `category_id` is
 * derived server-side (the shared rubro when every item has the same one,
 * NULL when mixed), never picked by the caller.
 *
 * fix/consolidado-sin-proveedor (2026-09-26) — user decision: "bloqueá
 * confirmar sin proveedor" (rediseno-cotizacion.md §6.2 exception, this is
 * the ONLY request that creates a consolidation round — there is no
 * "assign later" step for an item that entered with zero suppliers). The
 * `superRefine` below requires every id in `itemIds` to appear in
 * `assignments` with at least one supplier — enforced at this I/O boundary,
 * not a new domain transition. This is stricter than the single-SA
 * `IniciarCotizacionRequestSchema` above, which still allows an empty
 * `assignments` ("nada bloquea" stays for that flow).
 */
export const ConsolidationRoundCreateSchema = z
  .object({
    itemIds: z.array(z.string().uuid()).min(1),
    deadline: z.string().datetime(),
    assignments: z.array(z.object({ itemId: z.string().uuid(), supplierIds: z.array(z.string().uuid()) })).optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    const supplierCountByItem = new Map<string, number>();
    for (const a of data.assignments ?? []) {
      supplierCountByItem.set(a.itemId, (supplierCountByItem.get(a.itemId) ?? 0) + a.supplierIds.length);
    }
    const unassignedItemIds = data.itemIds.filter((id) => (supplierCountByItem.get(id) ?? 0) === 0);
    if (unassignedItemIds.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cada ítem del pedido consolidado necesita al menos un proveedor asignado.',
        path: ['assignments'],
      });
    }
  });
export type ConsolidationRoundCreate = z.infer<typeof ConsolidationRoundCreateSchema>;

/**
 * 201 response for a successful consolidated-round creation. `categoryId` is
 * nullable since requi-consolidacion-multirubro (2026-09-25) — null when the
 * round's items span more than one rubro.
 */
export const ConsolidationRoundCreatedResponseSchema = z.object({
  roundId: z.string().uuid(),
  consolidationNo: z.number(),
  categoryId: z.string().uuid().nullable(),
  caseIds: z.array(z.string().uuid()),
});
export type ConsolidationRoundCreatedResponse = z.infer<typeof ConsolidationRoundCreatedResponseSchema>;

/**
 * requi-cotizacion-consolidada-por-rubro (Slice C, task 3.18, design.md D5)
 * — GET /procurement/consolidation-rounds/:id. Item rows extend
 * `CaseItemResponseSchema` with per-SA traceability (same shape family as
 * `DemandItemSchema`, minus the tray-only `caseCode`/`caseTitle` duplication
 * naming — kept consistent here for the same reason).
 */
export const ConsolidationRoundItemResponseSchema = CaseItemResponseSchema.extend({
  caseCode: z.string(),
  caseTitle: z.string(),
  requesterName: z.string().nullable(),
  costCenterName: z.string().nullable(),
});
export type ConsolidationRoundItemResponse = z.infer<typeof ConsolidationRoundItemResponseSchema>;

export const ConsolidationRoundDetailResponseSchema = z.object({
  id: z.string().uuid(),
  // requi-consolidacion-multirubro (2026-09-25): null when the round's items
  // span more than one rubro — the frontend shows "Varios rubros" then.
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  consolidationNo: z.number(),
  status: z.enum(['abierta', 'cerrada']),
  deadline: z.string(),
  closedAt: z.string().nullable(),
  items: z.array(ConsolidationRoundItemResponseSchema),
  caseIds: z.array(z.string().uuid()),
  summedLines: z.array(SummedLineResponseSchema),
  invitations: z.array(z.object({ itemId: z.string().uuid(), supplierId: z.string().uuid() })),
  quotationCount: z.number(),
  lineCount: z.number(),
  suggestedSupplierIds: z.array(z.string().uuid()),
});
export type ConsolidationRoundDetailResponse = z.infer<typeof ConsolidationRoundDetailResponseSchema>;

/** GET /procurement/consolidation-rounds?status= — tray's "Pedidos consolidados" tab. */
export const ConsolidationRoundListRowSchema = z.object({
  id: z.string().uuid(),
  // requi-consolidacion-multirubro (2026-09-25): null when mixed rubros.
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  consolidationNo: z.number(),
  status: z.enum(['abierta', 'cerrada']),
  deadline: z.string(),
  closedAt: z.string().nullable(),
  itemCount: z.number(),
  caseCount: z.number(),
});
export const ConsolidationRoundListResponseSchema = z.object({ rounds: z.array(ConsolidationRoundListRowSchema) });
export type ConsolidationRoundListResponse = z.infer<typeof ConsolidationRoundListResponseSchema>;

/**
 * requi-cotizacion-consolidada-por-rubro (Slice C, task 3.19, design.md D12)
 * — response shape for a round-scoped action on
 * `POST /procurement/consolidation-rounds/:id/events`: every affected SA's
 * recomputed status, plus every touched item in its post-action shape.
 */
export const ConsolidationRoundEventResponseSchema = z.object({
  caseStatuses: z.record(z.string(), z.string()),
  items: z.array(CaseItemResponseSchema),
});
export type ConsolidationRoundEventResponse = z.infer<typeof ConsolidationRoundEventResponseSchema>;

/**
 * GET /procurement/rfq-rounds/:roundId/suppliers/:supplierId/pedido-de-cotizacion
 * — what one supplier is asked to quote in one round, for the buyer to send
 * by their own mail (PDF via the `.pdf` twin route, Excel built client-side).
 * Only the items that supplier is invited to; a consolidation round's lines
 * are summed per (itemMasterId, unit), never broken down per SA. `qty` is a
 * decimal string, same convention as every other qty field in this file.
 */
export const PedidoCotizacionResponseSchema = z.object({
  roundId: z.string().uuid(),
  roundNo: z.number(),
  isConsolidation: z.boolean(),
  deadline: z.string(),
  supplier: z.object({ id: z.string().uuid(), name: z.string() }),
  /** The SA this round belongs to; null for a consolidation round. */
  caseInfo: z.object({ code: z.string(), title: z.string() }).nullable(),
  lines: z.array(z.object({ lineNo: z.number(), description: z.string(), qty: z.string(), unit: z.string() })),
});
export type PedidoCotizacionResponse = z.infer<typeof PedidoCotizacionResponseSchema>;
